import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import ChipSelect from "@/components/ChipSelect";
import { Users, Plus, Trash2, ChevronDown, ChevronRight, KeyRound } from "lucide-react";

export default function UsersPanel() {
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("member");
  const [tokens, setTokens] = useState([]);
  const [userIds, setUserIds] = useState([]);
  const [expanded, setExpanded] = useState(null);

  const { data: users = [] } = useQuery({ queryKey: ["dashboard-users"], queryFn: async () => (await api.get("/dashboard-users")).data });
  const { data: tokenList = [] } = useQuery({ queryKey: ["ingest-tokens"], queryFn: async () => (await api.get("/ingest-tokens")).data });
  const { data: telemetryUsers = [] } = useQuery({ queryKey: ["telemetry-users"], queryFn: async () => (await api.get("/users")).data });

  const tokenOptions = tokenList.map((t) => t.label);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["dashboard-users"] });

  const create = useMutation({
    mutationFn: async (payload) => (await api.post("/dashboard-users", payload)).data,
    onSuccess: () => {
      invalidate();
      toast.success("User created");
      setEmail(""); setPassword(""); setRole("member"); setTokens([]); setUserIds([]);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to create user"),
  });

  const update = useMutation({
    mutationFn: async ({ id, patch }) => (await api.patch(`/dashboard-users/${id}`, patch)).data,
    onSuccess: () => { invalidate(); toast.success("User updated"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update user"),
  });

  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/dashboard-users/${id}`)).data,
    onSuccess: () => { invalidate(); toast.success("User deleted"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete user"),
  });

  const confirmDelete = (u) => {
    if (window.confirm(`Delete dashboard user ${u.email}? This cannot be undone.`)) {
      remove.mutate(u.id);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!email.trim() || password.length < 6) {
      toast.error("Email and a 6+ char password are required");
      return;
    }
    create.mutate({ email: email.trim(), password, role, assigned_tokens: tokens, assigned_user_ids: userIds });
  };

  const resetPassword = (u) => {
    const pw = window.prompt(`New password for ${u.email} (min 6 chars):`);
    if (pw == null) return;
    if (pw.length < 6) { toast.error("Password too short"); return; }
    update.mutate({ id: u.id, patch: { password: pw } });
  };

  const inpCls = "h-9 rounded-none bg-black/50 border-border font-mono text-xs focus:ring-1 focus:ring-white";

  return (
    <div className="bg-card border border-border" data-testid="users-panel">
      <div className="px-5 py-4 border-b border-border flex items-center gap-2">
        <Users className="w-4 h-4 text-white" />
        <div>
          <p className="overline">dashboard users</p>
          <h3 className="text-sm font-medium mt-1">Create members and scope what they can see</h3>
        </div>
      </div>

      {/* Create form */}
      <form onSubmit={submit} className="px-5 py-4 border-b border-border grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="create-user-form">
        <div className="space-y-2">
          <Label className="overline">Email</Label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="dev@example.com" data-testid="new-user-email" className={inpCls} />
        </div>
        <div className="space-y-2">
          <Label className="overline">Temp password (min 6)</Label>
          <Input type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="set a password" data-testid="new-user-password" className={inpCls} />
        </div>
        <div className="space-y-2">
          <Label className="overline">Role</Label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className={`${inpCls} w-[160px]`} data-testid="new-user-role"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none font-mono">
              <SelectItem value="member">member</SelectItem>
              <SelectItem value="admin">admin</SelectItem>
            </SelectContent>
          </Select>
          {role === "admin" && <p className="font-mono text-[11px] text-amber-500/80">admins see everything; assignments below are ignored</p>}
        </div>
        <div />
        <div className="space-y-2">
          <Label className="overline">Assigned ingest tokens</Label>
          <ChipSelect options={tokenOptions} selected={tokens} onChange={setTokens} emptyLabel="no tokens yet — create one below" testidPrefix="new-user-token" />
        </div>
        <div className="space-y-2">
          <Label className="overline">Assigned telemetry user.id(s)</Label>
          <ChipSelect options={telemetryUsers} selected={userIds} onChange={setUserIds} emptyLabel="no telemetry users yet" testidPrefix="new-user-uid" />
        </div>
        <div className="lg:col-span-2">
          <Button type="submit" disabled={create.isPending} data-testid="create-user-button" className="h-9 rounded-none bg-white text-black hover:bg-white/90 gap-1.5">
            <Plus className="w-3.5 h-3.5" /> Create user
          </Button>
        </div>
      </form>

      {/* Users table */}
      <div className="overflow-x-auto">
        <table className="w-full font-mono text-xs">
          <thead>
            <tr className="text-muted-foreground border-b border-border">
              {["", "Email", "Role", "Tokens", "User IDs", "Actions"].map((h) => (
                <th key={h} className="text-left font-normal px-3 py-2 whitespace-nowrap overline">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <React.Fragment key={u.id}>
                <tr className="border-b border-border/50 hover:bg-white/[0.03] transition-colors" data-testid={`user-row-${u.email}`}>
                  <td className="px-3 py-2.5">
                    <button onClick={() => setExpanded((e) => (e === u.id ? null : u.id))} data-testid={`edit-user-${u.email}`} className="text-muted-foreground hover:text-white">
                      {expanded === u.id ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    </button>
                  </td>
                  <td className="px-3 py-2.5 text-white">{u.email}</td>
                  <td className="px-3 py-2.5">
                    <span className={u.role === "admin" ? "text-amber-400" : "text-blue-400"}>{u.role}</span>
                  </td>
                  <td className="px-3 py-2.5 text-muted-foreground max-w-[220px] truncate">{u.role === "admin" ? "all" : (u.assigned_tokens.join(", ") || "—")}</td>
                  <td className="px-3 py-2.5 text-muted-foreground max-w-[220px] truncate">{u.role === "admin" ? "all" : (u.assigned_user_ids.join(", ") || "—")}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-3">
                      <button onClick={() => resetPassword(u)} data-testid={`reset-pw-${u.email}`} className="text-muted-foreground hover:text-white" title="Reset password">
                        <KeyRound className="w-4 h-4" />
                      </button>
                      <button onClick={() => confirmDelete(u)} data-testid={`delete-user-${u.email}`} className="text-muted-foreground hover:text-red-500" title="Delete user">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
                {expanded === u.id && (
                  <tr className="bg-black/40" data-testid={`user-edit-${u.email}`}>
                    <td colSpan={6} className="px-5 py-4 space-y-4">
                      {u.role === "admin" ? (
                        <p className="font-mono text-[11px] text-muted-foreground">Admins have full access; there is nothing to scope.</p>
                      ) : (
                        <EditAssignments u={u} tokenOptions={tokenOptions} telemetryUsers={telemetryUsers} onSave={(patch) => update.mutate({ id: u.id, patch })} saving={update.isPending} />
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EditAssignments({ u, tokenOptions, telemetryUsers, onSave, saving }) {
  const [tokens, setTokens] = useState(u.assigned_tokens || []);
  const [userIds, setUserIds] = useState(u.assigned_user_ids || []);
  return (
    <div className="space-y-4" data-testid={`assign-editor-${u.email}`}>
      <div className="space-y-2">
        <Label className="overline">Assigned ingest tokens</Label>
        <ChipSelect options={tokenOptions} selected={tokens} onChange={setTokens} testidPrefix={`edit-token-${u.email}`} />
      </div>
      <div className="space-y-2">
        <Label className="overline">Assigned telemetry user.id(s)</Label>
        <ChipSelect options={telemetryUsers} selected={userIds} onChange={setUserIds} testidPrefix={`edit-uid-${u.email}`} />
      </div>
      <Button onClick={() => onSave({ assigned_tokens: tokens, assigned_user_ids: userIds })} disabled={saving} data-testid={`save-assign-${u.email}`} className="h-9 rounded-none bg-white text-black hover:bg-white/90">
        {saving ? "Saving…" : "Save assignments"}
      </Button>
    </div>
  );
}
